/**
 * Separatore delle migliaia all'italiana: 1847 → "1.847"
 *
 * Il separatore si inserisce a mano invece che con `toLocaleString('it-IT')`,
 * che per l'italiano **non raggruppa i numeri di quattro cifre**: 1279 diventa
 * "1279" e 12790 diventa "12.790" (CLDR, `minimumGroupingDigits: 2`). È corretto
 * per la norma, ma non è quello che fa il mock v11 — che scrive "8.900 kWh" e
 * "€ 1.279" — né quello che faceva già `fmtEuro`, che il separatore lo mette da
 * sempre con questa stessa regex. Le due funzioni sorelle formattavano lo stesso
 * migliaio in due modi diversi a seconda di quale si chiamava.
 */
export function fmtNum(n: number): string {
	const rounded = Math.round(n);
	const digits = Math.abs(rounded)
		.toString()
		.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
	return rounded < 0 ? `-${digits}` : digits;
}

/**
 * Italian Euro, **senza simbolo**: 1847.50 → "1.847,50"
 *
 * Il simbolo non c'è per ragioni storiche, non per scelta: diverse viste lo
 * disegnano a parte (`<span>€</span>` accanto al numero, per dargli un corpo
 * diverso). Aggiungerlo qui lo raddoppierebbe là. Dove serve il simbolo dentro
 * la stringa, usa {@link fmtEuroSign}.
 */
export function fmtEuro(n: number): string {
	return n
		.toFixed(2)
		.replace('.', ',')
		.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/**
 * Euro col simbolo davanti, a cifra intera: 1847.50 → "€ 1.848".
 *
 * Niente centesimi, in nessun caso: sono importi che si leggono a colpo
 * d'occhio in una card o in una riga di elenco, e due decimali allungano il
 * numero senza dire niente a chi guarda. Dove serve il centesimo — un
 * rendiconto, un dettaglio di liquidazione — c'è {@link fmtEuro}, che il
 * chiamante compone col simbolo come preferisce.
 */
export function fmtEuroSign(n: number): string {
	return `€ ${fmtNum(n)}`;
}

export function fmtPct(n: number): string {
	return `${Math.round(n)}%`;
}

export function fmtKwh(n: number): string {
	return `${fmtNum(n)} kWh`;
}

export function fmtMwh(n: number): string {
	return `${(Math.round(n * 10) / 10).toLocaleString('it-IT')} MWh`;
}
